import React, { useCallback, useEffect, useRef, useState } from 'react'
import { LoaderBar } from 'components'
import {
    AppDangerButton,
    AppPrimaryButton,
    AppSecondaryButton,
} from 'elements'
import { useNotification } from 'hooks/toast'
import { TemplateService } from 'utility/services/template'
import { MasterAnswersService } from 'utility/services/master-answers'
import { RollbackMigrationModal } from './RollbackMigrationModal'
import {
    buildMigrationPayload,
    computeUnionItems,
    normalizeMigrationHistoryList,
    normalizeOptionSets,
} from './migration-utils'
import { getApiErrorMessage, slugifyListKey } from './utils'
import {
    ConflictRow,
    DoneCard,
    EmptyHint,
    HintBox,
    HistoryPanel,
    HistoryRow,
    ItemChip,
    MergeBuilder,
    MergeSourceRow,
    MergeTargetHead,
    MergeTargetPanel,
    MasterAnswersFieldGroup,
    MasterAnswersFieldLabel,
    MasterAnswersTextInput,
    PostCommitNote,
    Step4ItemsBox,
    Step4SafetyNote,
    Step4StatCard,
    Step4StatGrid,
    Step4Summary,
    Step4SummaryCount,
    Step4SummaryIcon,
    Step4SummaryMain,
    TemplateCardGrid,
    TemplatePickCard,
    WarnBanner,
    WizardCard,
    WizardCardBody,
    WizardCardBodyCompact,
    WizardCardHeader,
    WizardCardHeaderCompact,
    WizardNav,
    WizardStep,
    WizardStepper,
    WizardStepperCompact,
} from './styles'

const STEP_LABELS = [
    'Pick scope',
    'Select option sets',
    'Configure master',
    'Preview & commit',
]

const initialWizardState = companyId => ({
    step: 1,
    selectedTemplateIds: [],
    optionSets: [],
    selectedSetKeys: [],
    masterName: '',
    masterKey: '',
    canonicalChoices: {},
    preview: null,
    committed: false,
    committedListId: null,
    commitResult: null,
    companyId,
})

export const MigrationWizardTab = ({ companyId }) => {
    const { successToast, errorToast } = useNotification()
    const toastRef = useRef({ successToast, errorToast })
    toastRef.current = { successToast, errorToast }

    const [wizard, setWizard] = useState(() => initialWizardState(companyId))
    const [templates, setTemplates] = useState([])
    const [templatesLoading, setTemplatesLoading] = useState(true)
    const [optionSetsLoading, setOptionSetsLoading] = useState(false)
    const [previewLoading, setPreviewLoading] = useState(false)
    const [committing, setCommitting] = useState(false)
    const [history, setHistory] = useState([])
    const [historyLoading, setHistoryLoading] = useState(false)
    const [rollbackEntry, setRollbackEntry] = useState(null)
    const [rollbackSaving, setRollbackSaving] = useState(false)

    const selectedSets = wizard.optionSets.filter(s =>
        wizard.selectedSetKeys.includes(s.key),
    )
    const unionItems = computeUnionItems(
        selectedSets,
        wizard.canonicalChoices,
    )

    const loadTemplates = useCallback(async () => {
        setTemplatesLoading(true)
        try {
            const result = await TemplateService.templateList({
                PageNumber: 1,
                PageSize: 5000,
                SearchText: '',
            })
            setTemplates(
                (result?.Items || []).map(t => ({
                    id: t.TemplateUniqueId ?? t.Id,
                    name: t.TemplateName || t.Name,
                })),
            )
        } catch (err) {
            toastRef.current.errorToast(getApiErrorMessage(err))
        } finally {
            setTemplatesLoading(false)
        }
    }, [])

    const loadHistory = useCallback(async () => {
        setHistoryLoading(true)
        try {
            const data = await MasterAnswersService.getMigrationHistory(
                companyId,
            )
            setHistory(normalizeMigrationHistoryList(data))
        } catch (err) {
            toastRef.current.errorToast(getApiErrorMessage(err))
            setHistory([])
        } finally {
            setHistoryLoading(false)
        }
    }, [companyId])

    useEffect(() => {
        loadTemplates()
        loadHistory()
    }, [loadTemplates, loadHistory])

    const loadOptionSets = async templateIds => {
        setOptionSetsLoading(true)
        try {
            const data = await MasterAnswersService.getMigrationOptionSets(
                companyId,
                templateIds,
            )
            const sets = normalizeOptionSets(data)
            setWizard(prev => ({
                ...prev,
                optionSets: sets,
                selectedSetKeys: [],
            }))
        } catch (err) {
            toastRef.current.errorToast(getApiErrorMessage(err))
            setWizard(prev => ({ ...prev, optionSets: [] }))
        } finally {
            setOptionSetsLoading(false)
        }
    }

    const runPreview = async state => {
        const sets = state.optionSets.filter(s =>
            state.selectedSetKeys.includes(s.key),
        )
        const items = computeUnionItems(sets, state.canonicalChoices)
        const payload = buildMigrationPayload({
            companyId,
            masterName: state.masterName,
            masterKey: state.masterKey || slugifyListKey(state.masterName),
            selectedSets: sets,
            unionItems: items,
        })
        setPreviewLoading(true)
        try {
            const preview = await MasterAnswersService.previewMigration(
                payload,
            )
            setWizard(prev => ({ ...prev, preview }))
            return preview
        } catch (err) {
            toastRef.current.errorToast(getApiErrorMessage(err))
            return null
        } finally {
            setPreviewLoading(false)
        }
    }

    const toggleTemplate = (id, checked) => {
        setWizard(prev => {
            const ids = checked
                ? [...prev.selectedTemplateIds, id]
                : prev.selectedTemplateIds.filter(x => x !== id)
            const keys = prev.selectedSetKeys.filter(k => {
                const tid = k.split('-')[0]
                return ids.some(id => String(id) === String(tid))
            })
            return {
                ...prev,
                selectedTemplateIds: ids,
                selectedSetKeys: keys,
            }
        })
    }

    const toggleSet = (key, checked) => {
        setWizard(prev => ({
            ...prev,
            selectedSetKeys: checked
                ? [...prev.selectedSetKeys, key]
                : prev.selectedSetKeys.filter(k => k !== key),
        }))
    }

    const wizardNext = async () => {
        if (wizard.step === 1) {
            if (!wizard.selectedTemplateIds.length) {
                toastRef.current.errorToast(
                    'Pick at least one template card above',
                )
                return
            }
            await loadOptionSets(wizard.selectedTemplateIds)
            setWizard(prev => ({ ...prev, step: 2 }))
            return
        }
        if (wizard.step === 2) {
            if (wizard.selectedSetKeys.length < 2) {
                toastRef.current.errorToast(
                    'Click Add on at least 2 source rows on the left',
                )
                return
            }
            if (!wizard.masterName.trim()) {
                toastRef.current.errorToast(
                    'Fill in the Name in the right-side panel',
                )
                return
            }
            if (!wizard.masterKey.trim()) {
                toastRef.current.errorToast(
                    'Fill in the Key in the right-side panel',
                )
                return
            }
            setWizard(prev => ({ ...prev, step: 3 }))
            return
        }
        if (wizard.step === 3) {
            const unresolved = unionItems.filter(
                i => i.hasConflict && !i.resolved,
            )
            if (unresolved.length) {
                toastRef.current.errorToast(
                    `${unresolved.length} conflict(s) still to resolve`,
                )
                return
            }
            setWizard(prev => ({ ...prev, step: 4 }))
            const next = { ...wizard, step: 4 }
            await runPreview(next)
        }
    }

    const wizardBack = () => {
        if (wizard.committed) return
        setWizard(prev => ({
            ...prev,
            step: Math.max(1, prev.step - 1),
            preview: prev.step === 4 ? null : prev.preview,
        }))
    }

    const wizardReset = () => {
        setWizard(initialWizardState(companyId))
        loadHistory()
    }

    const wizardCommit = async () => {
        const { preview } = wizard
        if (preview?.WouldSucceed === false) {
            toastRef.current.errorToast(
                (preview.Warnings || []).join(' ') ||
                    'Preview blocked commit',
            )
            return
        }
        const payload = buildMigrationPayload({
            companyId,
            masterName: wizard.masterName,
            masterKey: wizard.masterKey,
            selectedSets,
            unionItems,
        })
        setCommitting(true)
        try {
            const result = await MasterAnswersService.commitMigration(payload)
            setWizard(prev => ({
                ...prev,
                committed: true,
                committedListId: result.MasterListId ?? result.masterListId,
                commitResult: result,
            }))
            toastRef.current.successToast(
                `Migration committed — ${result.ItemsInserted ?? unionItems.length} items`,
            )
            loadHistory()
        } catch (err) {
            toastRef.current.errorToast(getApiErrorMessage(err))
        } finally {
            setCommitting(false)
        }
    }

    const executeRollback = async () => {
        if (!rollbackEntry) return
        setRollbackSaving(true)
        try {
            await MasterAnswersService.rollbackMigration(
                rollbackEntry.masterListId,
            )
            toastRef.current.successToast(
                `Rolled back "${rollbackEntry.masterListName}"`,
            )
            setRollbackEntry(null)
            if (wizard.committedListId === rollbackEntry.masterListId) {
                wizardReset()
            } else {
                loadHistory()
            }
        } catch (err) {
            toastRef.current.errorToast(getApiErrorMessage(err))
        } finally {
            setRollbackSaving(false)
        }
    }

    const setCanonical = (displayText, score) => {
        setWizard(prev => {
            const next = { ...prev.canonicalChoices }
            if (score === null) delete next[displayText]
            else next[displayText] = { score }
            return { ...prev, canonicalChoices: next }
        })
    }

    const renderHistory = () => {
        if (historyLoading) {
            return <LoaderBar content="Loading migration history..." />
        }
        if (!history.length) return null
        const activeCount = history.filter(h => !h.isRolledBack).length
        return (
            <HistoryPanel>
                <div
                    style={{
                        padding: '12px 16px',
                        background: '#fafbfc',
                        borderBottom: '1px solid #e5e7eb',
                        fontWeight: 600,
                        fontSize: 13,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                    }}
                >
                    <span>📜 Recent migrations on this tenant</span>
                    <span
                        style={{
                            marginLeft: 'auto',
                            background: '#e8ecfb',
                            color: '#3853cc',
                            padding: '2px 9px',
                            borderRadius: 999,
                            fontSize: 11,
                            fontWeight: 700,
                        }}
                    >
                        {activeCount} active
                    </span>
                </div>
                {history.map(h => (
                    <HistoryRow key={h.id} $rolledBack={h.isRolledBack}>
                        <span
                            style={{
                                width: 36,
                                height: 36,
                                borderRadius: '50%',
                                background: h.isRolledBack
                                    ? '#6b7280'
                                    : '#10b981',
                                color: '#fff',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            {h.isRolledBack ? '↺' : '✓'}
                        </span>
                        <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, fontSize: 13.5 }}>
                                {h.masterListName}
                                {h.isRolledBack && (
                                    <span
                                        style={{
                                            marginLeft: 8,
                                            background: '#fee2e2',
                                            color: '#dc2626',
                                            padding: '2px 9px',
                                            borderRadius: 999,
                                            fontSize: 10,
                                            fontWeight: 700,
                                        }}
                                    >
                                        rolled back
                                    </span>
                                )}
                            </div>
                            <div
                                style={{
                                    fontSize: 11.5,
                                    color: '#6b7280',
                                    marginTop: 2,
                                }}
                            >
                                {h.masterListKey} · MasterListId{' '}
                                {h.masterListId}
                            </div>
                        </div>
                        <div
                            style={{
                                display: 'flex',
                                gap: 14,
                                fontSize: 12,
                            }}
                        >
                            <span>
                                <strong>{h.itemsInserted}</strong> items
                            </span>
                            <span>
                                <strong>{h.questionsUpdated}</strong>{' '}
                                questions
                            </span>
                        </div>
                        {!h.isRolledBack && (
                            <AppDangerButton
                                type="button"
                                onClick={() => setRollbackEntry(h)}
                            >
                                ↺ Roll back
                            </AppDangerButton>
                        )}
                    </HistoryRow>
                ))}
            </HistoryPanel>
        )
    }

    const renderStep1 = () => {
        const picked = wizard.selectedTemplateIds.length
        const setsByTpl = new Map()
        wizard.optionSets.forEach(s => {
            const arr = setsByTpl.get(s.templateUniqueId) || []
            arr.push(s)
            setsByTpl.set(s.templateUniqueId, arr)
        })

        return (
            <>
                <HintBox>
                    <span style={{ fontWeight: 700 }}>Step 1</span>
                    <span style={{ marginLeft: 'auto' }}>
                        Selection becomes{' '}
                        <code>templateIds</code> when you continue to step 2.
                    </span>
                </HintBox>
                <div
                    style={{
                        display: 'flex',
                        gap: 18,
                        alignItems: 'center',
                        marginBottom: 18,
                        padding: '14px 16px',
                        background: '#fafbfc',
                        borderRadius: 6,
                    }}
                >
                    <span style={{ fontSize: 24 }}>🏢</span>
                    <div style={{ flex: 1 }}>
                        <div
                            style={{
                                fontSize: 13,
                                color: '#4b5563',
                                marginBottom: 4,
                            }}
                        >
                            Tenant (CompanyId {companyId})
                        </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                        <div
                            style={{
                                fontSize: 26,
                                fontWeight: 700,
                                color: '#3853cc',
                            }}
                        >
                            {picked}
                        </div>
                        <div
                            style={{
                                fontSize: 11,
                                color: '#6b7280',
                                textTransform: 'uppercase',
                            }}
                        >
                            picked
                        </div>
                    </div>
                </div>
                {templatesLoading && (
                    <LoaderBar content="Loading templates..." />
                )}
                <h4 style={{ fontSize: 14, margin: '0 0 12px' }}>
                    Tap to pick a template
                </h4>
                <TemplateCardGrid>
                    {templates.map(t => {
                        const checked = wizard.selectedTemplateIds.includes(
                            t.id,
                        )
                        const sets = setsByTpl.get(t.id) || []
                        const chips = {}
                        sets.forEach(s => {
                            const label = s.questionText || 'Question'
                            chips[label] =
                                (chips[label] || 0) + s.items.length
                        })
                        const totalItems = sets.reduce(
                            (n, s) => n + s.items.length,
                            0,
                        )
                        return (
                            <TemplatePickCard
                                key={t.id}
                                type="button"
                                $checked={checked}
                                onClick={() => toggleTemplate(t.id, !checked)}
                            >
                                <span className="check">
                                    {checked ? '✓' : ''}
                                </span>
                                <div style={{ fontSize: 18, marginBottom: 8 }}>
                                    📋
                                </div>
                                <div
                                    style={{
                                        fontWeight: 600,
                                        marginBottom: 4,
                                    }}
                                >
                                    {t.name}
                                </div>
                                <div
                                    style={{
                                        fontSize: 12,
                                        color: '#6b7280',
                                    }}
                                >
                                    TemplateId {t.id}
                                    {sets.length
                                        ? ` · ${totalItems} options across ${sets.length} sets`
                                        : ''}
                                </div>
                                <div style={{ marginTop: 10 }}>
                                    {Object.entries(chips).map(
                                        ([name, count]) => (
                                            <ItemChip key={name}>
                                                {name} · {count}
                                            </ItemChip>
                                        ),
                                    )}
                                </div>
                            </TemplatePickCard>
                        )
                    })}
                </TemplateCardGrid>
            </>
        )
    }

    const renderStep2 = () => {
        const sets = wizard.optionSets
        const conflictCount = unionItems.filter(i => i.hasConflict).length

        return (
            <>
                <HintBox>
                    <span style={{ fontWeight: 700 }}>Step 2</span>
                    <span style={{ marginLeft: 'auto' }}>
                        Loads option sets for selected templates.
                    </span>
                </HintBox>
                {optionSetsLoading && (
                    <LoaderBar content="Loading option sets..." />
                )}
                {!optionSetsLoading && sets.length === 0 && (
                    <EmptyHint>No eligible option sets found.</EmptyHint>
                )}
                {!optionSetsLoading && sets.length > 0 && (
                    <MergeBuilder>
                        <div>
                            <h4 style={{ fontSize: 14, margin: '0 0 8px' }}>
                                Add option sets to your master
                            </h4>
                            <p
                                style={{
                                    fontSize: 12.5,
                                    color: '#6b7280',
                                    marginBottom: 12,
                                }}
                            >
                                Click <strong>Add</strong> on a row to include
                                its items. You need <strong>2 or more</strong>{' '}
                                sources to commit.
                            </p>
                            {sets.map(s => {
                                const added = wizard.selectedSetKeys.includes(
                                    s.key,
                                )
                                return (
                                    <MergeSourceRow key={s.key} $added={added}>
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 8,
                                                flexWrap: 'wrap',
                                            }}
                                        >
                                            <span>📁</span>
                                            <strong>{s.templateName}</strong>
                                            <span style={{ color: '#6b7280' }}>
                                                ›
                                            </span>
                                            <span
                                                style={{
                                                    background: '#f1f5f9',
                                                    padding: '2px 8px',
                                                    borderRadius: 3,
                                                    fontSize: 12,
                                                }}
                                            >
                                                {s.questionText}
                                            </span>
                                            <span
                                                style={{
                                                    marginLeft: 'auto',
                                                    fontSize: 11,
                                                    color: '#6b7280',
                                                }}
                                            >
                                                {s.items.length} items
                                            </span>
                                            <AppSecondaryButton
                                                type="button"
                                                size="mini"
                                                onClick={() =>
                                                    toggleSet(s.key, !added)
                                                }
                                            >
                                                {added ? '✓ Added' : '+ Add'}
                                            </AppSecondaryButton>
                                        </div>
                                        <div style={{ marginTop: 8 }}>
                                            {s.items.map((it, idx) => (
                                                <ItemChip key={idx}>
                                                    {it.displayText}
                                                    {it.score != null && (
                                                        <span
                                                            style={{
                                                                marginLeft: 4,
                                                                fontWeight: 600,
                                                            }}
                                                        >
                                                            {it.score}
                                                        </span>
                                                    )}
                                                </ItemChip>
                                            ))}
                                        </div>
                                    </MergeSourceRow>
                                )
                            })}
                        </div>
                        <MergeTargetPanel>
                            <MergeTargetHead>
                                <h4>📦 Your new master list</h4>
                                <p>Updates live as you add sources on the left.</p>
                            </MergeTargetHead>
                            <div style={{ padding: '16px 18px' }}>
                                <MasterAnswersFieldGroup>
                                    <MasterAnswersFieldLabel htmlFor="migration-master-name">
                                        Name
                                    </MasterAnswersFieldLabel>
                                    <MasterAnswersTextInput
                                        id="migration-master-name"
                                        name="migrationMasterName"
                                        type="text"
                                        autoComplete="off"
                                        autoCorrect="off"
                                        autoCapitalize="off"
                                        spellCheck={false}
                                        placeholder="e.g. Customers"
                                        value={wizard.masterName}
                                        onChange={e =>
                                            setWizard(prev => ({
                                                ...prev,
                                                masterName: e.target.value,
                                            }))
                                        }
                                    />
                                </MasterAnswersFieldGroup>
                                <MasterAnswersFieldGroup>
                                    <MasterAnswersFieldLabel htmlFor="migration-master-key">
                                        Key
                                    </MasterAnswersFieldLabel>
                                    <MasterAnswersTextInput
                                        id="migration-master-key"
                                        name="migrationMasterKey"
                                        type="text"
                                        autoComplete="off"
                                        autoCorrect="off"
                                        autoCapitalize="off"
                                        spellCheck={false}
                                        placeholder="e.g. customers"
                                        value={wizard.masterKey}
                                        onChange={e =>
                                            setWizard(prev => ({
                                                ...prev,
                                                masterKey: e.target.value,
                                            }))
                                        }
                                    />
                                </MasterAnswersFieldGroup>
                                <div
                                    style={{
                                        marginTop: 14,
                                        maxHeight: 280,
                                        overflowY: 'auto',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: 6,
                                    }}
                                >
                                    {unionItems.length === 0 ? (
                                        <div
                                            style={{
                                                padding: 24,
                                                textAlign: 'center',
                                                color: '#6b7280',
                                                fontSize: 12.5,
                                            }}
                                        >
                                            Add at least one source on the
                                            left to start.
                                        </div>
                                    ) : (
                                        unionItems.map(item => (
                                            <div
                                                key={item.displayText}
                                                style={{
                                                    display: 'flex',
                                                    padding: '8px 10px',
                                                    borderBottom:
                                                        '1px solid #eef0f3',
                                                    fontSize: 13,
                                                }}
                                            >
                                                <span style={{ flex: 1 }}>
                                                    {item.displayText}
                                                </span>
                                                {item.hasConflict ? (
                                                    <span
                                                        style={{
                                                            fontSize: 10,
                                                            background:
                                                                '#fef3c7',
                                                            color: '#92400e',
                                                            padding: '2px 7px',
                                                            borderRadius: 999,
                                                            fontWeight: 700,
                                                        }}
                                                    >
                                                        conflict
                                                    </span>
                                                ) : (
                                                    <span
                                                        style={{
                                                            fontSize: 11,
                                                            background:
                                                                '#f1f5f9',
                                                            padding: '2px 7px',
                                                            borderRadius: 999,
                                                        }}
                                                    >
                                                        {item.canonicalScore}
                                                    </span>
                                                )}
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                            <div
                                style={{
                                    borderTop: '1px solid #e5e7eb',
                                    padding: '10px 18px',
                                    fontSize: 12,
                                    display: 'flex',
                                    gap: 18,
                                }}
                            >
                                <span>
                                    <strong>
                                        {wizard.selectedSetKeys.length}
                                    </strong>{' '}
                                    sources
                                </span>
                                <span>
                                    <strong>{unionItems.length}</strong>{' '}
                                    items
                                </span>
                                {conflictCount > 0 && (
                                    <span style={{ marginLeft: 'auto' }}>
                                        <strong style={{ color: '#f59e0b' }}>
                                            {conflictCount}
                                        </strong>{' '}
                                        to resolve
                                    </span>
                                )}
                            </div>
                        </MergeTargetPanel>
                    </MergeBuilder>
                )}
            </>
        )
    }

    const renderStep3 = () => {
        const conflicts = unionItems.filter(i => i.hasConflict)
        const unresolved = conflicts.filter(i => !i.resolved)
        const settled = unionItems.filter(i => !i.hasConflict)

        if (!conflicts.length) {
            return (
                <div style={{ textAlign: 'center', padding: '48px 24px' }}>
                    <div style={{ fontSize: 48, marginBottom: 12 }}>🎉</div>
                    <h3 style={{ margin: '0 0 6px' }}>Nothing to resolve</h3>
                    <p style={{ color: '#6b7280', margin: 0 }}>
                        All {unionItems.length} items have consistent scores.
                        Click <strong>Next →</strong> to review and commit.
                    </p>
                </div>
            )
        }

        return (
            <>
                <HintBox>
                    <span style={{ fontWeight: 700 }}>Step 3</span>
                    <span style={{ marginLeft: 'auto' }}>
                        Resolves into CanonicalItems for Preview/Commit.
                    </span>
                </HintBox>
                <div
                    style={{
                        display: 'flex',
                        gap: 14,
                        alignItems: 'center',
                        padding: '14px 16px',
                        background: '#fafbfc',
                        borderRadius: 6,
                        marginBottom: 18,
                    }}
                >
                    <div style={{ fontSize: 28 }}>⚖️</div>
                    <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600 }}>
                            Same item, different scores
                        </div>
                        <div style={{ fontSize: 12.5, color: '#6b7280' }}>
                            Pick the score that should go on the master list.
                        </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                        <div
                            style={{
                                fontSize: 22,
                                fontWeight: 700,
                                color:
                                    unresolved.length === 0
                                        ? '#10b981'
                                        : '#f59e0b',
                            }}
                        >
                            {conflicts.length - unresolved.length}/
                            {conflicts.length}
                        </div>
                        <div style={{ fontSize: 11, color: '#6b7280' }}>
                            resolved
                        </div>
                    </div>
                </div>
                {conflicts.map(item => (
                    <ConflictRow
                        key={item.displayText}
                        $resolved={item.resolved}
                    >
                        <div style={{ fontWeight: 600, marginBottom: 8 }}>
                            {item.displayText}
                        </div>
                        {!item.resolved ? (
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                {item.uniqueScores.map(score => (
                                    <AppSecondaryButton
                                        key={score}
                                        type="button"
                                        onClick={() =>
                                            setCanonical(
                                                item.displayText,
                                                score,
                                            )
                                        }
                                    >
                                        Use score {score}
                                    </AppSecondaryButton>
                                ))}
                            </div>
                        ) : (
                            <AppSecondaryButton
                                type="button"
                                onClick={() =>
                                    setCanonical(item.displayText, null)
                                }
                            >
                                ↺ change choice
                            </AppSecondaryButton>
                        )}
                    </ConflictRow>
                ))}
                {settled.length > 0 && (
                    <p style={{ fontSize: 12.5, color: '#6b7280', marginTop: 16 }}>
                        ✓ {settled.length} item(s) already settled (consistent
                        across sets)
                    </p>
                )}
            </>
        )
    }

    const renderStep4 = () => {
        const { preview } = wizard
        const warnings = preview?.Warnings || preview?.warnings || []

        return (
            <>
                {previewLoading && (
                    <LoaderBar content="Running migration preview..." />
                )}
                {warnings.length > 0 && (
                    <WarnBanner>
                        <span>⚠️</span>
                        <div>
                            {warnings.map((w, i) => (
                                <div key={i}>{w}</div>
                            ))}
                            {preview?.WouldSucceed === false && (
                                <strong style={{ display: 'block', marginTop: 8 }}>
                                    Commit is blocked until warnings are
                                    resolved.
                                </strong>
                            )}
                        </div>
                    </WarnBanner>
                )}
                <Step4Summary>
                    <Step4SummaryIcon>📦</Step4SummaryIcon>
                    <Step4SummaryMain>
                        <h4>{wizard.masterName}</h4>
                        <div className="meta">
                            Key: {wizard.masterKey} · CompanyId {companyId}
                        </div>
                    </Step4SummaryMain>
                    <Step4SummaryCount>
                        <div className="num">{unionItems.length}</div>
                        <div className="label">items</div>
                    </Step4SummaryCount>
                </Step4Summary>
                <Step4StatGrid>
                    <Step4StatCard>
                        <div className="label">Master list</div>
                        <div className="value">1</div>
                        <div className="suffix">created</div>
                    </Step4StatCard>
                    <Step4StatCard>
                        <div className="label">Items</div>
                        <div className="value">
                            {preview?.WillInsertItems ?? unionItems.length}
                        </div>
                        <div className="suffix">inserted</div>
                    </Step4StatCard>
                    <Step4StatCard>
                        <div className="label">Questions</div>
                        <div className="value">
                            {preview?.WillUpdateQuestions ??
                                selectedSets.length}
                        </div>
                        <div className="suffix">linked</div>
                    </Step4StatCard>
                </Step4StatGrid>
                <Step4SafetyNote>
                    <strong>✓ Safe operation.</strong> In-progress audits keep
                    their frozen text. Old inline options stay in place — fully
                    reversible via Rollback.
                </Step4SafetyNote>
                <Step4ItemsBox open>
                    <summary>
                        Items going in ({unionItems.length})
                    </summary>
                    <div className="chips">
                        {unionItems.map(item => (
                            <ItemChip key={item.displayText}>
                                {item.displayText}
                                <span style={{ marginLeft: 4 }}>
                                    {item.canonicalScore}
                                </span>
                            </ItemChip>
                        ))}
                    </div>
                </Step4ItemsBox>
                <p
                    style={{
                        margin: '8px 0 0',
                        fontSize: 11.5,
                        color: '#6b7280',
                        textAlign: 'center',
                    }}
                >
                    When you click <strong>Commit migration</strong>, all of the
                    above happens in one database transaction.
                </p>
            </>
        )
    }

    const renderCommitted = () => {
        const result = wizard.commitResult || {}
        const itemsCount = result.ItemsInserted ?? unionItems.length
        const questionsCount =
            result.QuestionsUpdated ?? selectedSets.length
        return (
            <>
                <DoneCard>
                    <div className="check-mark">✓</div>
                    <h3>Migration complete</h3>
                    <p>
                        Master list{' '}
                        <strong>&quot;{wizard.masterName}&quot;</strong> (Id{' '}
                        {wizard.committedListId}) created with{' '}
                        <strong>{itemsCount}</strong> items.{' '}
                        <strong>{questionsCount}</strong> template question
                        {questionsCount === 1 ? ' was' : 's were'} linked.
                        <span className="subtext">
                            Future audits pull from the master at start-time.
                            In-progress audits keep their frozen text.
                        </span>
                    </p>
                </DoneCard>
                <PostCommitNote>
                    <span>ℹ️</span>
                    <div>
                        Click <strong>Rollback</strong> to undo this migration,
                        or <strong>Start another</strong> to begin a fresh
                        wizard run.
                    </div>
                </PostCommitNote>
            </>
        )
    }

    const renderStepBody = () => {
        if (wizard.committed) return renderCommitted()
        if (wizard.step === 1) return renderStep1()
        if (wizard.step === 2) return renderStep2()
        if (wizard.step === 3) return renderStep3()
        if (wizard.step === 4) return renderStep4()
        return null
    }

    const stepHeaders = [
        'Pick the templates you want to clean up',
        'Build your master list',
        'Resolve the few conflicts we found',
        wizard.committed ? 'Migration committed' : 'Ready to go?',
    ]

    return (
        <>
            <WarnBanner>
                <span>⚠️</span>
                <div>
                    Super Admin only. This wizard converts existing
                    per-template option sets into a single shared master list.{' '}
                    <strong>Existing in-progress audits are unaffected</strong>{' '}
                    (snapshot preserves them).
                </div>
            </WarnBanner>

            {renderHistory()}

            {wizard.step === 4 || wizard.committed ? (
                <WizardStepperCompact>
                    {STEP_LABELS.map((label, idx) => {
                        const num = idx + 1
                        const done = wizard.committed || wizard.step > num
                        const active =
                            !wizard.committed && wizard.step === num
                        return (
                            <WizardStep
                                key={label}
                                $done={done}
                                $active={active}
                            >
                                <span className="num">
                                    {done ? '✓' : num}
                                </span>
                                {label}
                            </WizardStep>
                        )
                    })}
                </WizardStepperCompact>
            ) : (
                <WizardStepper>
                    {STEP_LABELS.map((label, idx) => {
                        const num = idx + 1
                        const done = wizard.committed || wizard.step > num
                        const active =
                            !wizard.committed && wizard.step === num
                        return (
                            <WizardStep
                                key={label}
                                $done={done}
                                $active={active}
                            >
                                <span className="num">
                                    {done ? '✓' : num}
                                </span>
                                {label}
                            </WizardStep>
                        )
                    })}
                </WizardStepper>
            )}

            <WizardCard>
                {wizard.step === 4 || wizard.committed ? (
                    <>
                        <WizardCardHeaderCompact>
                            {stepHeaders[wizard.committed ? 3 : wizard.step - 1]}
                        </WizardCardHeaderCompact>
                        <WizardCardBodyCompact>
                            {renderStepBody()}
                        </WizardCardBodyCompact>
                    </>
                ) : (
                    <>
                        <WizardCardHeader>
                            {stepHeaders[wizard.step - 1]}
                        </WizardCardHeader>
                        <WizardCardBody>{renderStepBody()}</WizardCardBody>
                    </>
                )}
            </WizardCard>

            <WizardNav $compact={wizard.step === 4 || wizard.committed}>
                <span className="step-indicator">
                    {wizard.committed
                        ? 'Complete'
                        : `Step ${wizard.step} of 4`}
                </span>
                {!wizard.committed && (
                    <>
                        <AppSecondaryButton
                            type="button"
                            disabled={wizard.step <= 1}
                            onClick={wizardBack}
                        >
                            ← Back
                        </AppSecondaryButton>
                        {wizard.step < 4 && (
                            <AppPrimaryButton
                                type="button"
                                onClick={wizardNext}
                            >
                                Next →
                            </AppPrimaryButton>
                        )}
                        {wizard.step === 4 && (
                            <AppPrimaryButton
                                type="button"
                                loading={committing || previewLoading}
                                disabled={
                                    previewLoading ||
                                    wizard.preview?.WouldSucceed === false
                                }
                                onClick={wizardCommit}
                                style={{
                                    background: '#10b981 !important',
                                    ...(wizard.step === 4
                                        ? {
                                              fontSize: '13px',
                                              padding: '8px 14px',
                                          }
                                        : {}),
                                }}
                            >
                                ✓ Commit migration
                            </AppPrimaryButton>
                        )}
                    </>
                )}
                {wizard.committed && (
                    <>
                        <AppDangerButton
                            type="button"
                            onClick={() => {
                                const entry = history.find(
                                    h =>
                                        h.masterListId ===
                                        wizard.committedListId,
                                )
                                if (entry) {
                                    setRollbackEntry(entry)
                                } else {
                                    setRollbackEntry({
                                        masterListId: wizard.committedListId,
                                        masterListName: wizard.masterName,
                                        itemsInserted: unionItems.length,
                                        questionsUpdated:
                                            selectedSets.length,
                                    })
                                }
                            }}
                            style={{
                                fontSize: '13px',
                                padding: '8px 14px',
                            }}
                        >
                            ↺ Roll back this migration
                        </AppDangerButton>
                        <AppSecondaryButton
                            type="button"
                            onClick={wizardReset}
                            style={{
                                fontSize: '13px',
                                padding: '8px 14px',
                            }}
                        >
                            Start another
                        </AppSecondaryButton>
                    </>
                )}
            </WizardNav>

            <RollbackMigrationModal
                open={!!rollbackEntry}
                entry={rollbackEntry}
                saving={rollbackSaving}
                onClose={() => setRollbackEntry(null)}
                onConfirm={executeRollback}
            />
        </>
    )
}
